/* eslint-disable no-undef */
import { useCallback, useEffect, useState } from 'react';

/**
 *
 * @param n new status
 * @param min low threshold
 * @param max high threshold
 * @returns a value that is within the thresholds
 */
function clamp(n: number, min: number, max: number) {
  if (n < min) return min;

  if (n > max) return max;

  return n;
}

/**
 * Increments the status and returns it
 * @param status current status
 * @returns the status plus the new amount
 */
function getIncrementedValue(status: number) {
  let amount = 0;

  if (status > 1) {
    return 1;
  }

  if (status >= 0 && status < 0.2) {
    amount = 0.1;
  } else if (status >= 0.2 && status < 0.5) {
    amount = 0.04;
  } else if (status >= 0.5 && status < 0.8) {
    amount = 0.02;
  } else if (status >= 0.8 && status < 0.99) {
    amount = 0.005;
  } else {
    amount = 0;
  }

  // Returns status + amount if between the thresholds, otherwise return the min/max
  const newStatus = clamp(status + amount, 0, 0.994);

  return newStatus;
}

const clearIntervalSafe = (interval) => {
  if (interval) clearInterval(interval);
};

/**
 *
 * @param trickleSpeed
 * @returns finish - progresses the progress to the end
 * @returns start - initiates the progress
 * @returns status - current status of the progress (0 - 1)
 * @returns stop - stops the progress

 */
const useFakeProgress = (trickleSpeed: number) => {
  const [status, setStatus] = useState(0);
  const [startIntervalId, setStartIntervalId] = useState<
    NodeJS.Timer | undefined
  >(undefined);
  const [stopIntervalId, setStopIntervalId] = useState<
    NodeJS.Timer | undefined
  >(undefined);

  const trickle = useCallback(() => {
    setStatus((prev) => getIncrementedValue(prev));
  }, []);

  /**
   * Initiates the progress. Updates the cycle every $trickleSpeed
   */
  const start = useCallback(() => {
    const interval = setInterval(() => {
      trickle();
    }, trickleSpeed);

    setStartIntervalId(interval);
  }, [trickle, trickleSpeed]);

  /**
   * Pauses the progress
   */
  const stop = useCallback(() => {
    clearIntervalSafe(startIntervalId);
  }, [startIntervalId]);

  /**
   * Clears the start interval and increments the status every 50ms until it passes 1
   */
  const finish = useCallback(() => {
    clearIntervalSafe(startIntervalId);

    const interval = setInterval(() => {
      setStatus((prev) => {
        const newStatus = prev + 0.05;

        if (newStatus >= 1) {
          clearInterval(interval);
        }

        return Math.min(newStatus, 1);
      });
    }, 50);

    setStopIntervalId(interval);
  }, [startIntervalId]);

  /**
   * Clears interval on un-mount
   */
  useEffect(
    () => () => {
      clearIntervalSafe(startIntervalId);
      clearIntervalSafe(stopIntervalId);
    },
    [startIntervalId, stopIntervalId]
  );

  return {
    finish,
    start,
    status,
    stop,
  };
};

export default useFakeProgress;
